import React, { useCallback, useEffect, useState } from "react";
import {
  confirmCashEntitlementDisbursement,
  getAssignedCashEntitlementDisbursement,
  reportCashEntitlementNotDisbursed,
} from "../../api/cashEntitlements";
import { mapCashEntitlementRow } from "../../utils/cashEntitlementMapper";
import {
  cashEntitlementSourceLabel,
  formatCashAmount,
  formatCashDate,
} from "../../utils/cashEntitlement";
import "../advance/AdvanceDisbursementRouting.css";

export default function CashEntitlementConfirm() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [confirmingId, setConfirmingId] = useState(null);
  const [busyAction, setBusyAction] = useState("");
  const [actionError, setActionError] = useState("");

  const loadRows = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const payload = await getAssignedCashEntitlementDisbursement();
      const list = (Array.isArray(payload) ? payload : payload?.items || []).map(
        mapCashEntitlementRow,
      );
      setRows(list);
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر تحميل المستحقات بانتظار تأكيد الصرف",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleConfirm = async (row) => {
    if (!row?.id || confirmingId) return;
    const ok = window.confirm(
      `تأكيد أن الموظف ${row.employeeName || ""} استلم مبلغ ${formatCashAmount(row.amount)}؟`,
    );
    if (!ok) return;
    setConfirmingId(row.id);
    setBusyAction("confirm");
    setActionError("");
    try {
      await confirmCashEntitlementDisbursement(row.id);
      await loadRows();
    } catch (err) {
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر تأكيد صرف المستحقات",
      );
    } finally {
      setConfirmingId(null);
      setBusyAction("");
    }
  };

  const handleNotDisbursed = async (row) => {
    if (!row?.id || confirmingId) return;
    const ok = window.confirm(
      `تأكيد أن مستحقات ${row.employeeName || "الموظف"} بمبلغ ${formatCashAmount(row.amount)} لم تُصرف؟ ستُعاد للمحاسب العام.`,
    );
    if (!ok) return;
    setConfirmingId(row.id);
    setBusyAction("not-disbursed");
    setActionError("");
    try {
      await reportCashEntitlementNotDisbursed(row.id);
      await loadRows();
    } catch (err) {
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر الإبلاغ أن المستحقات لم تُصرف",
      );
    } finally {
      setConfirmingId(null);
      setBusyAction("");
    }
  };

  return (
    <div className="adv-disburse" dir="rtl">
      <div className="adv-disburse-header">
        <div>
          <h2 className="adv-disburse-title">تأكيد صرف المستحقات النقدية</h2>
          <p className="adv-disburse-subtitle">
            المستحقات الموجّهة إليك أو إلى كاشيرية فرعك بانتظار تسليم المبلغ
            نقداً. بعد التأكيد يُسجَّل الصرف بدون خصم من المرتب.
          </p>
        </div>
        <button
          type="button"
          className="adv-disburse-refresh"
          onClick={loadRows}
          disabled={loading || Boolean(confirmingId)}
        >
          تحديث
        </button>
      </div>

      {actionError ? (
        <div className="adv-disburse-error-banner" style={{ marginBottom: 14 }}>
          {actionError}
        </div>
      ) : null}

      {loading ? (
        <div className="adv-disburse-empty">جاري التحميل...</div>
      ) : loadError ? (
        <div className="adv-disburse-error-banner">{loadError}</div>
      ) : rows.length === 0 ? (
        <div className="adv-disburse-empty">
          لا توجد مستحقات بانتظار تأكيد الصرف حالياً
        </div>
      ) : (
        <div className="adv-disburse-list">
          {rows.map((row) => (
            <div key={row.id} className="adv-disburse-card">
              <div className="adv-disburse-card-header">
                <div>
                  <div className="adv-disburse-employee-name">
                    {row.employeeName || "—"}
                  </div>
                  <div className="adv-disburse-employee-meta">
                    {row.employeeCode || ""}
                    {row.departmentName ? ` · ${row.departmentName}` : ""}
                    {row.branchName ? ` · ${row.branchName}` : ""}
                  </div>
                </div>
                <span className="adv-disburse-badge">بانتظار تأكيد الاستلام</span>
              </div>
              <div className="adv-disburse-details">
                <div className="adv-disburse-detail-row">
                  <span className="adv-disburse-detail-label">المبلغ:</span>
                  <span>{formatCashAmount(row.amount)}</span>
                </div>
                <div className="adv-disburse-detail-row">
                  <span className="adv-disburse-detail-label">المصدر:</span>
                  <span>{cashEntitlementSourceLabel(row.source)}</span>
                </div>
                <div className="adv-disburse-detail-row">
                  <span className="adv-disburse-detail-label">تاريخ التوجيه:</span>
                  <span>{formatCashDate(row.disbursementAssignedAt)}</span>
                </div>
                {row.reason ? (
                  <div className="adv-disburse-detail-row">
                    <span className="adv-disburse-detail-label">السبب:</span>
                    <span>{row.reason}</span>
                  </div>
                ) : null}
              </div>
              <div className="adv-disburse-actions">
                <button
                  type="button"
                  className="adv-disburse-btn danger"
                  onClick={() => handleNotDisbursed(row)}
                  disabled={Boolean(confirmingId)}
                >
                  {confirmingId === row.id && busyAction === "not-disbursed"
                    ? "جاري الإرجاع..."
                    : "لم يتم الصرف"}
                </button>
                <button
                  type="button"
                  className="adv-disburse-btn"
                  onClick={() => handleConfirm(row)}
                  disabled={Boolean(confirmingId)}
                >
                  {confirmingId === row.id && busyAction === "confirm"
                    ? "جاري التأكيد..."
                    : "تأكيد الاستلام"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
